import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BillingPage.css';

const STATUS_LABELS = {
  unbilled:  { label: 'Unbilled',  color: '#8898AA' },
  invoiced:  { label: 'Invoiced',  color: '#F59E0B' },
  paid:      { label: 'Paid',      color: '#10B981' },
  waived:    { label: 'Waived',    color: '#A8B8C8' },
};

const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BillingPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [records, setRecords]     = useState([]);
  const [clients, setClients]     = useState([]);
  const [summary, setSummary]     = useState({ total: 0, paid: 0, outstanding: 0, count: 0 });
  const [loading, setLoading]     = useState(true);
  const [isPro, setIsPro]         = useState(false);
  const [planChecked, setPlanChecked] = useState(false);
  const [defaultRate, setDefaultRate] = useState('');
  const [savingRate, setSavingRate]   = useState(false);

  // Filters
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom]     = useState('');
  const [filterTo, setFilterTo]         = useState('');

  // New record form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRecord, setNewRecord] = useState({
    clientId: '', date: new Date().toISOString().split('T')[0],
    sessionTitle: '', durationMinutes: '', rate: '', notes: '',
  });
  const [addingRecord, setAddingRecord] = useState(false);

  // Fetch records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterClient) params.set('clientId', filterClient);
      if (filterStatus) params.set('status', filterStatus);
      if (filterFrom)   params.set('from', filterFrom);
      if (filterTo)     params.set('to', filterTo);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/billing-records?${params}`,
        { headers: authHeaders }
      );
      setRecords(res.data.records || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error('[BillingPage] fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [filterClient, filterStatus, filterFrom, filterTo]);

  // Fetch clients + user default rate
  useEffect(() => {
    const init = async () => {
      try {
        const [clientsRes, userRes, planRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, { headers: authHeaders }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, { headers: authHeaders }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/billing/status`, { headers: authHeaders }),
        ]);
        setClients(clientsRes.data || []);
        setDefaultRate(userRes.data.defaultSessionRate || '');
        setNewRecord(prev => ({ ...prev, rate: userRes.data.defaultSessionRate || '' }));
        const plan = planRes.data.plan?.toLowerCase() || 'free';
        setIsPro(['pro','circle_member','circle_3','circle_5','circle_10'].includes(plan));
      } catch (err) {
        console.error('[BillingPage] init:', err);
      } finally {
        setPlanChecked(true);
      }
    };
    init();
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Save default rate
  const handleSaveRate = async () => {
    setSavingRate(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        { defaultSessionRate: parseFloat(defaultRate) || 0 },
        { headers: authHeaders }
      );
    } catch (err) {
      console.error('[BillingPage] save rate:', err);
    } finally {
      setSavingRate(false);
    }
  };

  // Update record status
  const handleStatusChange = async (id, status) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/billing-records/${id}`,
        { status },
        { headers: authHeaders }
      );
      setRecords(prev => prev.map(r => r._id === id ? { ...r, status } : r));
      // Recalculate summary
      const updated = records.map(r => r._id === id ? { ...r, status } : r);
      const total = updated.reduce((s, r) => s + r.amount, 0);
      const paid  = updated.filter(r => r.status === 'paid').reduce((s, r) => s + r.amount, 0);
      const outstanding = updated.filter(r => r.status !== 'paid' && r.status !== 'waived').reduce((s, r) => s + r.amount, 0);
      setSummary(prev => ({ ...prev, total, paid, outstanding }));
    } catch (err) {
      console.error('[BillingPage] status change:', err);
    }
  };

  // Delete record
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this billing record?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/billing-records/${id}`,
        { headers: authHeaders }
      );
      setRecords(prev => prev.filter(r => r._id !== id));
    } catch (err) {
      console.error('[BillingPage] delete:', err);
    }
  };

  // Add record
  const handleAddRecord = async (e) => {
    e.preventDefault();
    if (!newRecord.clientId || !newRecord.rate) return;
    setAddingRecord(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/billing-records`,
        { ...newRecord, rate: parseFloat(newRecord.rate), durationMinutes: parseInt(newRecord.durationMinutes) || 0 },
        { headers: authHeaders }
      );
      setRecords(prev => [res.data, ...prev]);
      setShowAddForm(false);
      setNewRecord({ clientId: '', date: new Date().toISOString().split('T')[0], sessionTitle: '', durationMinutes: '', rate: defaultRate, notes: '' });
      fetchRecords();
    } catch (err) {
      console.error('[BillingPage] add:', err);
    } finally {
      setAddingRecord(false);
    }
  };

  // Export CSV
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterClient) params.set('clientId', filterClient);
      if (filterStatus) params.set('status', filterStatus);
      if (filterFrom)   params.set('from', filterFrom);
      if (filterTo)     params.set('to', filterTo);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/billing-records/export?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[BillingPage] export error:', err);
    }
  };

  return (
    <div className="bp-page">

      {/* Nav */}
      <button className="bp-back-btn" onClick={() => navigate('/dashboard')}>← Dashboard</button>

      {/* ── Paywall ── */}
      {!planChecked ? (
        <div className="bp-loading">Checking access...</div>
      ) : !isPro ? (
        <div className="bp-paywall">
          <div className="bp-paywall-icon">💳</div>
          <h2 className="bp-paywall-title">Billing is a Pro feature</h2>
          <p className="bp-paywall-desc">
            Track session revenue, set per-client rates, generate reports, and export to CSV.
            Upgrade to Pro to unlock the full billing suite.
          </p>
          <button className="bp-paywall-btn" onClick={() => navigate('/pricing')}>
            Upgrade to Pro →
          </button>
          <p className="bp-paywall-sub">Pro · Circle plans · All Circle members</p>
        </div>
      ) : (
        <>
      <div className="bp-header">
        <div>
          <h1 className="bp-title">Billing</h1>
          <p className="bp-subtitle">Track sessions and revenue across clients</p>
        </div>
        <div className="bp-header-actions">
          <button className="bp-export-btn" onClick={handleExport}>↓ Export CSV</button>
          <button className="bp-add-btn" onClick={() => setShowAddForm(!showAddForm)}>
            {showAddForm ? '✕ Cancel' : '+ Add session'}
          </button>
        </div>
      </div>

      {/* Default rate setting */}
      <div className="bp-card bp-rate-card">
        <p className="bp-card-label">Default session rate</p>
        <div className="bp-rate-row">
          <div className="bp-rate-input-wrap">
            <span className="bp-dollar">$</span>
            <input
              className="bp-rate-input"
              type="number"
              min="0"
              step="0.01"
              value={defaultRate}
              onChange={e => setDefaultRate(e.target.value)}
              placeholder="0.00"
            />
            <span className="bp-per">per session</span>
          </div>
          <button className="bp-save-rate-btn" onClick={handleSaveRate} disabled={savingRate}>
            {savingRate ? 'Saving...' : 'Save default'}
          </button>
        </div>
        <p className="bp-rate-hint">Used when no per-client rate is set. Override per client in the client dashboard.</p>
      </div>

      {/* Summary cards */}
      <div className="bp-summary">
        <div className="bp-summary-card">
          <p className="bp-summary-label">Total billed</p>
          <p className="bp-summary-value">{fmt(summary.total)}</p>
          <p className="bp-summary-sub">{summary.count} session{summary.count !== 1 ? 's' : ''}</p>
        </div>
        <div className="bp-summary-card green">
          <p className="bp-summary-label">Paid</p>
          <p className="bp-summary-value">{fmt(summary.paid)}</p>
        </div>
        <div className="bp-summary-card coral">
          <p className="bp-summary-label">Outstanding</p>
          <p className="bp-summary-value">{fmt(summary.outstanding)}</p>
        </div>
      </div>

      {/* Add record form */}
      {showAddForm && (
        <div className="bp-card bp-add-form">
          <p className="bp-card-label">Add session</p>
          <form onSubmit={handleAddRecord} className="bp-form">
            <div className="bp-form-row">
              <div className="bp-form-field">
                <label>Client</label>
                <select value={newRecord.clientId} onChange={e => setNewRecord(p => ({ ...p, clientId: e.target.value }))} required>
                  <option value="">Select client</option>
                  {clients.map(c => (
                    <option key={c._id} value={c._id}>{c.firstName} {c.lastName}</option>
                  ))}
                </select>
              </div>
              <div className="bp-form-field">
                <label>Date</label>
                <input type="date" value={newRecord.date} onChange={e => setNewRecord(p => ({ ...p, date: e.target.value }))} required />
              </div>
            </div>
            <div className="bp-form-row">
              <div className="bp-form-field">
                <label>Session title</label>
                <input type="text" value={newRecord.sessionTitle} onChange={e => setNewRecord(p => ({ ...p, sessionTitle: e.target.value }))} placeholder="e.g. Weekly check-in" />
              </div>
              <div className="bp-form-field">
                <label>Duration (min)</label>
                <input type="number" value={newRecord.durationMinutes} onChange={e => setNewRecord(p => ({ ...p, durationMinutes: e.target.value }))} placeholder="60" min="0" />
              </div>
            </div>
            <div className="bp-form-row">
              <div className="bp-form-field">
                <label>Rate ($)</label>
                <input type="number" value={newRecord.rate} onChange={e => setNewRecord(p => ({ ...p, rate: e.target.value }))} placeholder="0.00" min="0" step="0.01" required />
              </div>
              <div className="bp-form-field">
                <label>Notes</label>
                <input type="text" value={newRecord.notes} onChange={e => setNewRecord(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>
            <button type="submit" className="bp-submit-btn" disabled={addingRecord}>
              {addingRecord ? 'Adding...' : 'Add session'}
            </button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bp-filters">
        <select className="bp-filter-select" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="">All clients</option>
          {clients.map(c => <option key={c._id} value={c._id}>{c.firstName} {c.lastName}</option>)}
        </select>
        <select className="bp-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="unbilled">Unbilled</option>
          <option value="invoiced">Invoiced</option>
          <option value="paid">Paid</option>
          <option value="waived">Waived</option>
        </select>
        <input type="date" className="bp-filter-date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} placeholder="From" />
        <input type="date" className="bp-filter-date" value={filterTo} onChange={e => setFilterTo(e.target.value)} placeholder="To" />
        {(filterClient || filterStatus || filterFrom || filterTo) && (
          <button className="bp-clear-btn" onClick={() => { setFilterClient(''); setFilterStatus(''); setFilterFrom(''); setFilterTo(''); }}>
            Clear filters
          </button>
        )}
      </div>

      {/* Records table */}
      <div className="bp-card bp-table-card">
        {loading ? (
          <div className="bp-loading">Loading billing records...</div>
        ) : records.length === 0 ? (
          <div className="bp-empty">
            <p>💳</p>
            <h3>No billing records yet</h3>
            <p>Records are created automatically when transcription sessions are saved, or add them manually above.</p>
          </div>
        ) : (
          <div className="bp-table-wrap">
            <table className="bp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Session</th>
                  <th>Duration</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r._id}>
                    <td className="bp-td-date">
                      {new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="bp-td-client">
                      <p className="bp-client-name">{r.clientId?.firstName} {r.clientId?.lastName}</p>
                      {r.clientId?.company && <p className="bp-client-co">{r.clientId.company}</p>}
                    </td>
                    <td className="bp-td-session">{r.sessionTitle || '—'}</td>
                    <td className="bp-td-dur">
                      {r.durationMinutes > 0 ? `${r.durationMinutes} min` : '—'}
                    </td>
                    <td className="bp-td-amount">{fmt(r.amount)}</td>
                    <td className="bp-td-status">
                      <select
                        className="bp-status-select"
                        value={r.status}
                        onChange={e => handleStatusChange(r._id, e.target.value)}
                        style={{ color: STATUS_LABELS[r.status]?.color }}
                      >
                        {Object.entries(STATUS_LABELS).map(([val, { label }]) => (
                          <option key={val} value={val}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="bp-td-actions">
                      <button className="bp-delete-btn" onClick={() => handleDelete(r._id)} title="Delete">🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </> )} {/* end isPro */}
    </div>
  );
};

export default BillingPage;